import type { FC, ReactNode } from 'react'

import { Minus, TrendingDown, TrendingUp } from 'lucide-react'
import { cn } from '@/lib/utils'

export type DeltaTone = 'positive' | 'negative' | 'neutral'

interface DeltaBadgeProps {
    value: string
    direction: 'up' | 'down' | 'flat'
    tone: DeltaTone
    className?: string
}

const TONES: Record<DeltaTone, string> = {
    positive: 'bg-success/12 text-success',
    negative: 'bg-destructive/10 text-destructive',
    neutral: 'bg-muted text-muted-foreground'
}

const TONE_LABELS: Record<DeltaTone, string> = {
    positive: 'membaik',
    negative: 'memburuk',
    neutral: 'stabil'
}

// Change indicator: always an icon plus text, never color alone
export const DeltaBadge: FC<DeltaBadgeProps> = ({ value, direction, tone, className }): ReactNode => {
    const Icon = direction === 'up' ? TrendingUp : direction === 'down' ? TrendingDown : Minus

    return (
        <span className={cn('inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-semibold tabular-nums', TONES[tone], className)}>
            <Icon className='size-3.5' aria-hidden='true' />
            {value}
            <span className='sr-only'>({TONE_LABELS[tone]})</span>
        </span>
    )
}

// Works out direction and tone from a numeric change. Set lowerIsBetter for churn.
export const getDelta = (change: number, lowerIsBetter = false, threshold = 0.05): { direction: 'up' | 'down' | 'flat', tone: DeltaTone } => {
    if (Math.abs(change) < threshold) return { direction: 'flat', tone: 'neutral' }

    const direction = change > 0 ? 'up' : 'down'
    const improving = lowerIsBetter ? change < 0 : change > 0

    return { direction, tone: improving ? 'positive' : 'negative' }
}
